var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Main subject rules (issue #76113).
 *
 * The main subject is evaluated per subject scheme (ONIX codelist 27): a
 * scheme holds at most one main subject and the flag of one scheme never
 * affects another (a title may have a Thema and a BISAC main subject).
 * Keywords (20) and Thema qualifiers (94–99) are never the main subject. The
 * only subject of a scheme is the main subject; several subjects of a scheme
 * without a main subject are allowed, the form only recommends choosing one.
 *
 * The backend applies the same rules (TitleSubjectRules in mod-br-agency).
 */
import { isThemaQualifierScheme } from "./themaSubjectRules";
export var KEYWORDS_SCHEME = "20";
var schemeOf = function (subject) { var _a; return ((_a = subject === null || subject === void 0 ? void 0 : subject.scheme) === null || _a === void 0 ? void 0 : _a.value) || undefined; };
/** Whether a subject of the scheme may be flagged as the main subject. */
export var canBeMainSubject = function (scheme) {
    return scheme !== KEYWORDS_SCHEME && !isThemaQualifierScheme(scheme);
};
/**
 * Subjects grouped by scheme value, in the order of first appearance. Plain
 * arrays on purpose: the stripes build targets ES5 without
 * downlevelIteration, so Map/Set iterators must not be spread.
 */
var groupByScheme = function (subjects) {
    var groups = [];
    subjects.forEach(function (subject) {
        var scheme = schemeOf(subject);
        if (!scheme)
            return;
        var group = groups.find(function (candidate) { return candidate.scheme === scheme; });
        if (group) {
            group.subjects.push(subject);
        }
        else {
            groups.push({ scheme: scheme, subjects: [subject] });
        }
    });
    return groups;
};
/** Marks the only subject of the scheme as main when the scheme allows it. */
var markSoleSubjectAsMain = function (subjects, scheme) {
    if (!scheme || !canBeMainSubject(scheme))
        return subjects;
    var indexes = subjects
        .map(function (subject, index) { return (schemeOf(subject) === scheme ? index : -1); })
        .filter(function (index) { return index >= 0; });
    if (indexes.length !== 1 || subjects[indexes[0]].mainSubject) {
        return subjects;
    }
    return subjects.map(function (subject, index) {
        return index === indexes[0] ? __assign(__assign({}, subject), { mainSubject: true }) : subject;
    });
};
/**
 * Saves a subject (appends it when `index` is null, replaces the subject at
 * `index` otherwise) and applies the main subject rules: a subject of a
 * keyword or qualifier scheme loses the main flag, a new main subject clears
 * the previous main of the SAME scheme only, and the only subject of a scheme
 * (the saved one's and, when its scheme changed, the old one's) becomes main.
 *
 * @returns a new array; the input is not changed
 */
export var saveSubjectWithMainRules = function (subjects, item, index) {
    var scheme = schemeOf(item);
    var saved = __assign(__assign({}, item), { mainSubject: canBeMainSubject(scheme) && !!item.mainSubject });
    var oldScheme = index === null ? undefined : schemeOf(subjects[index]);
    var result = index === null
        ? __spreadArray(__spreadArray([], subjects, true), [saved], false) : subjects.map(function (subject, i) { return (i === index ? saved : subject); });
    var savedIndex = index === null ? result.length - 1 : index;
    if (saved.mainSubject) {
        result = result.map(function (subject, i) {
            return i !== savedIndex && subject.mainSubject && schemeOf(subject) === scheme
                ? __assign(__assign({}, subject), { mainSubject: false }) : subject;
        });
    }
    result = markSoleSubjectAsMain(result, scheme);
    if (oldScheme !== scheme) {
        result = markSoleSubjectAsMain(result, oldScheme);
    }
    return result;
};
/**
 * Removes the subject at `index`; when exactly one subject of its scheme
 * remains, that one becomes the main subject.
 *
 * @returns a new array; the input is not changed
 */
export var removeSubjectWithMainRules = function (subjects, index) {
    return markSoleSubjectAsMain(subjects.filter(function (_, i) { return i !== index; }), schemeOf(subjects[index]));
};
/**
 * Schemes allowing a main subject that hold several subjects and none of them
 * is main — the form recommends choosing one (non-blocking warning).
 */
export var schemesMissingMainSubject = function (subjects) {
    return groupByScheme(subjects !== null && subjects !== void 0 ? subjects : [])
        .filter(function (group) {
        return canBeMainSubject(group.scheme) &&
            group.subjects.length > 1 &&
            !group.subjects.some(function (subject) { return subject.mainSubject; });
    })
        .map(function (group) { return group.scheme; });
};
/** Schemes with more than one main subject (invalid). */
export var schemesWithMultipleMainSubjects = function (subjects) {
    return groupByScheme(subjects !== null && subjects !== void 0 ? subjects : [])
        .filter(function (group) {
        return group.subjects.filter(function (subject) { return subject.mainSubject; })
            .length > 1;
    })
        .map(function (group) { return group.scheme; });
};
/** Keyword and Thema qualifier schemes carrying a main subject (invalid). */
export var schemesWithForbiddenMainSubject = function (subjects) {
    return groupByScheme(subjects !== null && subjects !== void 0 ? subjects : [])
        .filter(function (group) {
        return !canBeMainSubject(group.scheme) &&
            group.subjects.some(function (subject) { return subject.mainSubject; });
    })
        .map(function (group) { return group.scheme; });
};
